'use client';

import { cn } from '@/utils';
import { ISimulationPlanningCard, IPersona, TSimulationType } from '@/types';
import { SimulationScenarioInstructions } from './components/SimulationScenarioInstructions';
import { SimulationScenarioPageHeader } from './components/SimulationScenarioPageHeader';
import { SimulationScenarioPlanningCards } from './components/SimulationScenarioPlanningCards';
import { SimulationScenarioSlideoutModal } from '@/components/Simulations';
import React, { useState } from 'react';

/**
 * Props for SimulationScenario component
 */
export interface SimulationScenarioProps {
  /**
   * The certification title (e.g., "Something: Skill 1")
   */
  certificationTitle?: string;

  /**
   * The main scenario title
   */
  title?: string;

  /**
   * The main scenario type
   */
  type?: TSimulationType;

  /**
   * The scenario category or type
   */
  category?: string;

  /**
   * Callback for starting the role-play
   */
  onStartRolePlay?: () => void;

  /**
   * Additional className
   */
  className?: string;

  /**
   * Planning cards to display
   */
  planningCards?: ISimulationPlanningCard[];

  /**
   * Instructions for the simulation
   */
  instructions?: string;

  /**
   * Personas available for the simulation
   */
  personas?: IPersona[];

  /**
   * The simulation ID
   */
  simulationId?: number;

  /**
   * Loading state
   */
  isLoading?: boolean;
}

export const SimulationScenario: React.FC<SimulationScenarioProps> = ({
  category,
  certificationTitle,
  className,
  instructions,
  isLoading = true,
  onStartRolePlay,
  personas = [],
  planningCards = [],
  simulationId,
  title: simulationTitle,
  type,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeCard, setActiveCard] = useState<ISimulationPlanningCard | null>(null);
  const [_ignored, setActivePersona] = useState<IPersona | null>(null);
  const [animatingStepId, setAnimatingStepId] = useState<number | null>(null);

  /**
   * Handle opening the modal with the selected card.
   */
  const handleOpenModal = (card: ISimulationPlanningCard) => {
    setActiveCard(card);
    setActivePersona(personas.find((p) => p.uuid === card.personaId) || null);
    setIsModalOpen(true);
  };

  /**
   * Handle closing the modal.
   */
  const handleCloseModal = () => {
    setActiveCard(null);
    setActivePersona(null);
    setIsModalOpen(false);
  };

  /**
   * Move modal progress to the next planning card.
   */
  const handleProgressNext = () => {
    if (!activeCard) return;

    const currentIndex = planningCards.findIndex((c) => c.stepNumber === activeCard.stepNumber);
    if (currentIndex >= 0 && currentIndex < planningCards.length - 1) {
      setAnimatingStepId(activeCard.stepNumber);
      setActiveCard(planningCards[currentIndex + 1]);
    }
  };

  /**
   * Move modal progress to the previous planning card or selected completed step.
   */
  const handleProgressPrevious = (stepId?: number) => {
    if (!activeCard) return;
    setAnimatingStepId(null);

    // If a specific stepId is provided, navigate directly to that card.
    if (stepId) {
      return setActiveCard(planningCards.find((c) => c.stepNumber === stepId) || activeCard);
    }

    // Otherwise, go to the previous card in sequence.
    const currentIndex = planningCards.findIndex((c) => c.stepNumber === activeCard.stepNumber);
    if (currentIndex > 0) {
      setActiveCard(planningCards[currentIndex - 1]);
    }
  };

  // Render
  return (
    <div
      className={cn('relative mx-auto flex h-full flex-col', className)}
      data-simulation-id={simulationId}
      data-testid="simulation-scenario"
    >
      <div className="flex h-full flex-col space-y-6">
        {/* Header Section */}
        <SimulationScenarioPageHeader
          certificationTitle={certificationTitle}
          isLoading={isLoading}
          simulationDescription={category}
          simulationTitle={simulationTitle}
          simulationType={type}
        />

        {/* Planning Cards Section */}
        <SimulationScenarioPlanningCards
          planningCards={planningCards}
          isLoading={isLoading}
          onCardActionClick={handleOpenModal}
        />

        {/* Instructions & CTA Section */}
        <SimulationScenarioInstructions
          isLoading={isLoading}
          onStartRolePlay={onStartRolePlay}
          simulationInstructions={instructions}
          simulationType={type}
        />
      </div>

      {/* Modal for displaying detailed information about the selected card */}
      <SimulationScenarioSlideoutModal
        activeCard={activeCard}
        animatingStepId={animatingStepId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onComplete={onStartRolePlay}
        onNext={handleProgressNext}
        onPrevious={handleProgressPrevious}
        planningCards={planningCards}
      />
    </div>
  );
};

SimulationScenario.displayName = 'SimulationScenario';

export default SimulationScenario;
